import axios from "axios";

// Backend base URL. In production set REACT_APP_API_URL to your API origin
// (e.g. https://api.aashapurasafahouse.com); locally it falls back to the dev server.
const LOCALHOST = "http://localhost:5454";

export const API_BASE_URL = process.env.REACT_APP_API_URL || LOCALHOST;

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.defaults.headers.post["Content-Type"] = "application/json";

// Attach the latest JWT on every request so freshly-issued tokens
// (e.g. right after login, without a page reload) are always used.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("jwt");
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// Multipart uploads can't go through `api`: its default POST Content-Type is
// application/json, which would stop axios from setting the multipart boundary.
// So they use the bare axios instance — but /upload and /upload/video are
// admin-only, so the JWT still has to be attached explicitly.
export const uploadFormData = (path, formData) =>
  axios.post(`${API_BASE_URL}${path}`, formData, {
    headers: { Authorization: `Bearer ${localStorage.getItem("jwt")}` },
  });

// Auth failures surfaced by the API are handled here.
const handleBlocked = (error) => {
  const status = error.response?.status;
  const code = error.response?.data?.code;

  // Blocked account -> hard eject to the storefront, logged out. This is a
  // security action: the user must not keep interacting with the app.
  if (status === 403 && code === "ACCOUNT_BLOCKED") {
    localStorage.removeItem("jwt");
    if (window.location.pathname !== "/") {
      window.location.replace("/?blocked=1");
    }
    return Promise.reject(error);
  }

  // Expired/invalid session -> just drop the token and let the app fall back to
  // the logged-out (guest) state on the CURRENT page. We deliberately do NOT
  // redirect: otherwise a stale token discovered during page-load hydration
  // (getUser/getCart run on every page) would bounce the user to home on every
  // refresh. The 30-min idle timeout in App.js still does an explicit redirect.
  if (status === 401 && code === "SESSION_EXPIRED") {
    localStorage.removeItem("jwt");
  }

  return Promise.reject(error);
};

// Register on both the `api` instance AND the global axios default, because
// several actions (getUser, cart, image upload) call `axios` directly rather
// than through `api` — all of them must trigger the block-logout.
api.interceptors.response.use((response) => response, handleBlocked);
axios.interceptors.response.use((response) => response, handleBlocked);

export default api;
